import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import { splitFaceWithSegments } from '../../core/sketch/faceSplit';
import { arcLoopExact, circularLoop } from '../../core/sketch/curves';
import { planeBasis } from '../../core/sketch/triangulate';
import type { InferenceResult } from '../../core/sketch/inference';
import { loopFromPoints, useSketchToolEnv } from './sketchInput';
import { registerSketchTool } from './sketchToolBus';
import { commitClosedLoop, hitFaceRef, startFaceRef, type FaceRef } from './SketchDrawTools';

/**
 * Advanced draw tools — Freehand, Rotated Rectangle, 3-Point Arc and Pie,
 * completing the SketchUp draw set on top of the shared stroke plumbing
 * (active-plane following, inference, VCB exact values, Escape cancel).
 */

// ============================== FREEHAND ==============================

/**
 * Freehand: hold-drag draws an irregular polyline; release commits it.
 * On a face the whole stroke etches/splits it; in empty space the segments
 * land in the loose-edge graph (a closed stroke auto-forms a face through
 * detectClosedRegions, exactly like the Line tool's closure path).
 */
export function useSketchFreehandTool() {
    const env = useSketchToolEnv();
    const faceRef = useRef<FaceRef>(null);
    const drawingRef = useRef(false);
    const pointsRef = useRef<THREE.Vector3[]>([]);
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(hit.point.clone());
                if (!drawingRef.current) return;
                // Skip micro-jitter: a minimum world spacing driven by the
                // camera distance keeps the stroke smooth at every zoom.
                const last = pointsRef.current[pointsRef.current.length - 1];
                const minStep = Math.max(0.02, env.camera.position.distanceTo(hit.point) * 0.004);
                if (last && hit.point.distanceTo(last) < minStep) return;
                pointsRef.current = [...pointsRef.current, hit.point.clone()];
                setPoints(pointsRef.current);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = startFaceRef(hit);
                drawingRef.current = true;
                pointsRef.current = [hit.point.clone()];
                setPoints(pointsRef.current);
            },
        });
        return () => registerSketchTool(null);
    }, [env]);

    // Commit on pointer release (window-level so drags leaving the canvas
    // still finish), Escape discards the stroke.
    useEffect(() => {
        const commit = (): void => {
            if (!drawingRef.current) return;
            drawingRef.current = false;
            const stroke = pointsRef.current;
            if (stroke.length >= 2) {
                const closed = stroke[0].distanceTo(stroke[stroke.length - 1]) < 0.2;
                const chain = closed ? [...stroke.slice(0, -1), stroke[0]] : stroke;
                const face = faceRef.current;
                if (face) {
                    const store = useSketchStore.getState();
                    const solid = store.solids[face.solidId];
                    const target = solid?.faces.find((f) => f.id === face.faceId);
                    if (solid && target) {
                        const loop = loopFromPoints(chain);
                        const result = splitFaceWithSegments(target, [loop]);
                        if (result.ok) {
                            // Sibling faces survive the split (see commitClosedLoop).
                            const faces = solid.faces.flatMap((f) => (f.id === target.id ? result.faces : [f]));
                            store.updateSolid(face.solidId, { ...solid, faces }, 'Freehand');
                        }
                    }
                } else {
                    const store = useSketchStore.getState();
                    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(strokeNormal(stroke), stroke[0]);
                    for (let i = 1; i < chain.length; i++) {
                        const [ta, tb] = loopFromPoints([chain[i - 1], chain[i]]);
                        store.addLooseSegment(ta, tb, plane, 'Freehand');
                    }
                }
            }
            pointsRef.current = [];
            setPoints([]);
        };
        const onUp = (): void => commit();
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            drawingRef.current = false;
            pointsRef.current = [];
            setPoints([]);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    return { points, cursor, drawing: drawingRef.current };
}

/** Best-fit plane normal of a stroke: dominant world axis by spread. */
function strokeNormal(stroke: THREE.Vector3[]): THREE.Vector3 {
    const box = new THREE.Box3().setFromPoints(stroke);
    const size = box.getSize(new THREE.Vector3());
    const min = Math.min(size.x, size.y, size.z);
    if (min === size.z) return new THREE.Vector3(0, 0, 1);
    if (min === size.x) return new THREE.Vector3(1, 0, 0);
    return new THREE.Vector3(0, 1, 0);
}

// ============================== ROTATED RECTANGLE ==============================

/**
 * Rotated Rectangle: three clicks — corner, first edge (length + direction),
 * second edge. The second edge may leave the first edge's plane: the loop
 * is the rectangle spanned by u (first edge) and the perpendicular
 * component of the third point, all in the plane defined by the three
 * clicks — Sketch's tilted-face rectangle.
 */
export function useSketchRotatedRectTool() {
    const env = useSketchToolEnv();
    const faceRef = useRef<FaceRef>(null);
    const [p1, setP1] = useState<THREE.Vector3 | null>(null);
    const [p2, setP2] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, p1 ? [p1] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = p1 ? hitFaceRef(faceRef, hit) : startFaceRef(hit);
                const res = env.infer(hit, p1 ? [p1] : [], null);
                if (!p1) {
                    setP1(res.point.clone());
                    return;
                }
                if (!p2) {
                    if (res.point.distanceTo(p1) < 1e-6) return;
                    setP2(res.point.clone());
                    return;
                }
                // Third click: rectangle in the p1/p2/p3 plane.
                const u = p2.clone().sub(p1);
                const uLen = u.length();
                if (uLen < 1e-9) return;
                u.divideScalar(uLen);
                const raw = res.point.clone().sub(p1);
                const v = raw.clone().addScaledVector(u, -raw.dot(u));
                const vLen = v.length();
                if (vLen < 1e-9) return;
                v.divideScalar(vLen);
                const normal = new THREE.Vector3().crossVectors(u, v).normalize();
                const loop = [
                    p1.clone(),
                    p2.clone(),
                    p2.clone().addScaledVector(v, vLen),
                    p1.clone().addScaledVector(v, vLen),
                ];
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, p1);
                commitClosedLoop(loop, plane, faceRef.current, 'Rotated Rectangle');
                setP1(null);
                setP2(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, p1, p2]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setP1(null);
            setP2(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { p1, p2, cursor };
}

// ============================== 3-POINT ARC ==============================

/**
 * 3-Point Arc: start → point ON the arc → end (the middle click is the
 * bulge). Exact circumcircle through the three clicks, like the 2-Point
 * Arc engine — only the click order and preview differ.
 */
export function useSketchArc3ptTool() {
    const env = useSketchToolEnv();
    const faceRef = useRef<FaceRef>(null);
    const [start, setStart] = useState<THREE.Vector3 | null>(null);
    const [mid, setMid] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, start ? [start] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = start ? hitFaceRef(faceRef, hit) : startFaceRef(hit);
                const res = env.infer(hit, start ? [start] : [], null);
                if (!start) {
                    setStart(res.point.clone());
                    return;
                }
                if (!mid) {
                    if (res.point.distanceTo(start) < 1e-6) return;
                    setMid(res.point.clone());
                    return;
                }
                const plane = env.planeOf(hit);
                const exact = arcLoopExact(start, mid, res.point, 20);
                if (exact) {
                    commitClosedLoop(exact.loop, plane, faceRef.current, '3 Point Arc', exact.meta);
                }
                setStart(null);
                setMid(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, start, mid]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setStart(null);
            setMid(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { start, mid, cursor };
}

// ============================== PIE ==============================

/**
 * Pie: center → radius point → sweep end. The closed face is the circular
 * sector (center + arc + back to center); the sweep angle follows the
 * drag direction and can be typed in degrees into the VCB before the last
 * click (negative values sweep the other way).
 */
export function useSketchPieTool() {
    const env = useSketchToolEnv();
    const faceRef = useRef<FaceRef>(null);
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [radiusPoint, setRadiusPoint] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, center ? [center] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = center ? hitFaceRef(faceRef, hit) : startFaceRef(hit);
                const res = env.infer(hit, center ? [center] : [], null);
                if (!center) {
                    setCenter(res.point.clone());
                    return;
                }
                if (!radiusPoint) {
                    if (res.point.distanceTo(center) < 1e-6) return;
                    setRadiusPoint(res.point.clone());
                    return;
                }
                // Third click: sector face on the active plane.
                const plane = env.planeOf(hit);
                const normal = plane.normal.clone().normalize();
                const radius = center.distanceTo(radiusPoint);
                if (radius < 1e-6) return;
                const { u, v } = planeBasis(normal);
                const angleOf = (p: THREE.Vector3): number => {
                    const d = p.clone().sub(center);
                    return Math.atan2(d.dot(v), d.dot(u));
                };
                const a0 = angleOf(radiusPoint);
                let a1 = angleOf(res.point);
                // Signed sweep: keep the drag side (default CCW), VCB
                // override in degrees commits an exact angle.
                let sweep = a1 - a0;
                while (sweep <= -Math.PI) sweep += Math.PI * 2;
                while (sweep > Math.PI) sweep -= Math.PI * 2;
                const vcb = env.consumeVcb();
                if (vcb !== null) sweep = (vcb * Math.PI) / 180;
                const segments = Math.max(6, Math.ceil((Math.abs(sweep) / (Math.PI * 2)) * 32));
                const arc = circularLoop(center, radius, normal, a0, a0 + sweep, segments);
                if (arc.length >= 2) {
                    commitClosedLoop([center.clone(), ...arc], plane, faceRef.current, 'Pie');
                }
                setCenter(null);
                setRadiusPoint(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, center, radiusPoint]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCenter(null);
            setRadiusPoint(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { center, radiusPoint, cursor };
}
